import moment from 'moment';
import { useCallback, useMemo } from 'react';

import { DateFilters } from 'types';

type UsePeriodGuardParams<T extends DateFilters> = {
  filters?: T;
  setFilters: (filters: T) => void;
  maxPeriodDays: number;
  // A function, not a value — computed on reset so a long-lived SPA session doesn't reset to a stale date
  getDefaultFrom: () => string;
};

// The backend rejects periods wider than maxPeriodDays (SPAY-5210) — firing a request the backend is
// bound to reject would replace an explanation with a load error, so the UI must stop and explain
// instead. Mirrors the guard first written for ProviderConversionTab, now shared by every statistics tab.
// Applied regardless of the statistics engine: only the ClickHouse backend enforces the limit, but the UI
// doesn't know which engine serves the environment, and such wide periods are just as heavy on Postgres.
export function usePeriodGuard<T extends DateFilters>({
  filters,
  setFilters,
  maxPeriodDays,
  getDefaultFrom,
}: UsePeriodGuardParams<T>) {
  const isPeriodTooWide = useMemo(() => {
    if (!filters?.from) {
      return false;
    }
    const to = filters.to ? moment(filters.to) : moment();
    return to.diff(moment(filters.from), 'days', true) > maxPeriodDays;
  }, [filters, maxPeriodDays]);

  const handleResetPeriod = useCallback(() => {
    setFilters({ ...filters, from: getDefaultFrom(), to: undefined } as T);
  }, [filters, setFilters, getDefaultFrom]);

  return { isPeriodTooWide, handleResetPeriod };
}
